import { useMemo } from "react";
import type { Catalog, SeasonKey } from "../types";
import type { SongChoice } from "../state";
import { suggestSongs } from "../lib/songs";
import { Field, Select, TextInput, IconButton } from "../ui/controls";
import { Icons } from "../ui/icons";
import { YouTubeLink } from "../ui/YouTubeLink";
import { ScoreLink } from "../ui/ScoreLink";
import { SongAutocomplete } from "./SongAutocomplete";

const ANNOTATIONS = ["", "feuille", "chorale seule", "projeté", "carnet", "page"];

export function SongPicker({
  label,
  moment,
  season,
  catalog,
  value,
  onChange,
  onRegen,
  regenerating,
}: {
  label: string;
  moment: string;
  season: SeasonKey;
  catalog: Catalog;
  value: SongChoice;
  onChange: (c: SongChoice) => void;
  onRegen?: () => void;
  regenerating?: boolean;
}) {
  const ranked = useMemo(() => suggestSongs(catalog, moment, season), [catalog, moment, season]);
  const top = ranked[0];

  // Point the Partition / Écouter buttons at the chant's own score / video when we
  // have a link for it (Notion repertoire, or the scraped parish repertoire).
  const catSong = catalog.songs.find((s) => s.title === value.title || s.aliases.includes(value.title));
  const scoreHref = catSong?.links?.partition || catSong?.links?.notion;
  const videoHref = catSong?.links?.youtube;

  const suggestion =
    top && top.score > 1 && top.song.title !== value.title ? (
      <span>
        Suggestion : <strong>{top.song.title}</strong> — {top.reasons.join(", ")}{" "}
        <button
          type="button"
          onClick={() => onChange({ title: top.song.title, annotation: top.song.annotations?.[0] || value.annotation, page: "" })}
          style={{ border: "none", background: "transparent", color: "var(--accent-text)", fontFamily: "var(--font-ui)", fontSize: "var(--text-sm)", fontWeight: 600, cursor: "pointer", padding: 0 }}
        >
          utiliser
        </button>
      </span>
    ) : undefined;

  const action = (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {onRegen && (
        <IconButton aria-label="Proposer un chant" variant="sacred" size="sm" onClick={onRegen} disabled={regenerating}>
          <Icons.Refresh size={14} />
        </IconButton>
      )}
      <ScoreLink title={value.title} href={scoreHref} />
      <YouTubeLink title={value.title} href={videoHref} />
    </div>
  );
  // The reason (why this song fits the readings) is persisted on the song; show it
  // as a plain note under the field, without flagging that it was AI-generated.
  const hint = value.reason ? (
    <span style={{ fontStyle: "italic" }}>{value.reason}</span>
  ) : (
    suggestion
  );

  return (
    <Field label={label} action={action} hint={hint}>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <div style={{ flex: 3, minWidth: 200 }}>
          <SongAutocomplete
            value={value.title}
            catalog={catalog}
            moment={moment}
            season={season}
            onPick={(title, song, carnet) =>
              onChange(
                carnet
                  ? { title, annotation: "carnet", page: carnet.page }
                  : { title, annotation: song?.annotations?.[0] ?? value.annotation, page: song ? "" : value.page }
              )
            }
          />
        </div>

        <div style={{ flex: 1, minWidth: 130 }}>
          <Select value={value.annotation} onChange={(v) => onChange({ ...value, annotation: v })}>
            {ANNOTATIONS.map((a) => (
              <option key={a} value={a}>
                {a === "" ? "(sans mention)" : a === "page" ? "page…" : a}
              </option>
            ))}
          </Select>
        </div>

        {(value.annotation === "page" || value.annotation === "carnet") && (
          <div style={{ flex: "0 0 92px" }}>
            <TextInput value={value.page} placeholder="1042" onChange={(v) => onChange({ ...value, page: v })} />
          </div>
        )}
      </div>
    </Field>
  );
}
